import { GlobalState } from "context/GlobalContext";
import React, { useEffect, useState } from "react";
import { pythonVmInstance } from "utils/AxiosInstances/RareboxPythonInstance";
import RenderInputField from "components/EboStoreV4/CommonComponents/RenderInputFiled/RenderInputFiled";
import { filterColArry } from "./data";
import { getColDefaultValue, isColumnDefault } from "components/EboStoreV4/data";
import { constraintsOptions, selectOptKeyName } from "data/globalData";
import FileSaver from "file-saver";
import QueryFilterPanel from "components/CustomQueriesDashboard/ReusableComponents/QueryFilterPanel/QueryFilterPanel";

const CASCADE = ["SHOP", "BRAND", "DIVISION", "DEPARTMENT", "SEASON"];
const CHILD_FIELDS = ["BRAND", "DIVISION", "DEPARTMENT", "SEASON"];
const FIELD_PARENTS = {
	BRAND: ["SHOP"],
	DIVISION: ["BRAND"],
	DEPARTMENT: ["DIVISION"],
	SEASON: ["DEPARTMENT"],
};
const ATTRIBUTE_PARENTS = {
	BRAND: [],
	DIVISION: ["BRAND"],
	DEPARTMENT: ["BRAND", "DIVISION"],
	SEASON: ["BRAND", "DIVISION", "DEPARTMENT"],
};

const listsMatch = (left, right) => {
	const a = Array.isArray(left) ? left : [];
	const b = Array.isArray(right) ? right : [];
	return a.length === b.length && a.every((value, index) => value === b[index]);
};

const optionList = (values) => {
	const unique = new Set();
	values.forEach((value) => {
		if (value) unique.add(String(value));
	});
	return [...unique]
		.sort((a, b) => a.localeCompare(b))
		.map((value) => ({ name: value, key: value }));
};

const optionsForField = (attributes, selections, field) => {
	const parents = ATTRIBUTE_PARENTS[field] || [];
	const unlocked = parents.every((parent) => (selections?.[parent] || []).length > 0);
	if (field === "BRAND" && !(selections?.SHOP || []).length) return [];
	if (!unlocked) return [];
	const rows = attributes.filter((row) =>
		parents.every((parent) => (selections[parent] || []).includes(row[parent]))
	);
	return optionList(rows.map((row) => row[field]));
};

const withSelectList = (column, list) => ({
	...column,
	[selectOptKeyName]: {
		...(column[selectOptKeyName] || {}),
		list,
	},
});

const ReturnReport = () => {
	const { setSnackBar, snackBar, setSnackBarOpen, setLoadingOpen } = GlobalState();
	const [loading, setLoading] = useState(false);
	const [filterColumns, setFilterColumns] = useState(filterColArry);
	const [attributes, setAttributes] = useState([]);

	const defaultData = filterColArry?.reduce((acc, row) => {
		acc[row.key] = getColDefaultValue(row);
		return acc;
	}, {});

	const [filterData, setFilterDataState] = useState({ ...defaultData });

	const setFilterData = (next) => {
		setFilterDataState((prev) => {
			const updated = typeof next === "function" ? next(prev) : next;
			const changedIndex = CASCADE.findIndex((key) => !listsMatch(prev?.[key], updated?.[key]));
			if (changedIndex === -1) return updated;
			const cleared = { ...updated };
			for (let index = changedIndex + 1; index < CASCADE.length; index += 1) {
				cleared[CASCADE[index]] = [];
			}
			return cleared;
		});
	};

	const fetchDropdownValues = async () => {
		setLoadingOpen(true);
		try {
			const res = await pythonVmInstance.get("/custom_queries/ecomm/returns/get_filters");
			const shopOptions = res?.data?.SHOP || [];
			setAttributes(Array.isArray(res?.data?.ATTRIBUTES) ? res.data.ATTRIBUTES : []);
			setFilterColumns((allColumns) =>
				allColumns.map((row) => (row.key === "SHOP" ? withSelectList(row, shopOptions) : row))
			);
			setLoadingOpen(false);
		} catch (error) {
			setLoadingOpen(false);
			setSnackBarOpen(true);
			setSnackBar({
				...snackBar,
				message: "unable to fetch values for dropdown",
				severity: "error",
				anchorOrigin: { horizontal: "center", vertical: "top" },
			});
		}
	};

	useEffect(() => {
		fetchDropdownValues();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		setFilterColumns((columns) =>
			columns.map((column) => {
				if (!CHILD_FIELDS.includes(column.key)) return column;
				return withSelectList(column, optionsForField(attributes, filterData, column.key));
			})
		);
	}, [attributes, filterData]);

	const downloadDisabled = filterColArry.some(
		(attr) =>
			attr?.constraints?.[constraintsOptions.NOTNULL.key] === true &&
			isColumnDefault(attr, filterData?.[attr?.key])
	);

	const handleRunQueryClick = async (e) => {
		e.preventDefault();
		setLoading(true);
		const pendingFields = [];
		filterColArry?.forEach((attr) => {
			if (isColumnDefault(attr, filterData?.[attr?.key]) && attr?.constraints?.[constraintsOptions.NOTNULL.key] === true)
				pendingFields.push(attr);
		});

		if (pendingFields?.length) {
			setSnackBarOpen(true);
			setSnackBar({
				...snackBar,
				message: `Enter values for ${pendingFields.map((row) => row?.name)}`,
				severity: "error",
				anchorOrigin: { horizontal: "center", vertical: "top" },
			});
			setLoading(false);
			return;
		}

		try {
			await pythonVmInstance({
				url: "/custom_queries/ecomm/returns/download",
				headers: {
					"Content-Type": "application/json",
				},
				method: "post",
				responseType: "blob",
				data: {
					...filterData,
				},
			}).then((res) => {
				const url = URL.createObjectURL(res.data);
				FileSaver.saveAs(url, `ECOMM_RETURN_REPORT_${new Date().toISOString().split("T")[0]}.csv`);
				setSnackBarOpen(true);
				setSnackBar({
					...snackBar,
					message: "Downloaded the file",
					severity: "success",
					anchorOrigin: { horizontal: "center", vertical: "top" },
				});
				setLoading(false);
			});
		} catch (error) {
			setSnackBarOpen(true);
			setSnackBar({
				...snackBar,
				message: `failed to run query ${error}`,
				severity: "error",
				anchorOrigin: { horizontal: "center", vertical: "top" },
			});
			setLoading(false);
		}
	};

	return (
		<QueryFilterPanel
			onSubmit={handleRunQueryClick}
			loading={loading}
			submitDisabled={downloadDisabled}
			submitLabel="Download"
			hint="Pick Shop, then Brand, then Division, then Department, then Season. Each list only shows values that belong to the previous choice. Download stays off until the dates and every dropdown have a selection.">
			{filterColumns?.map((col) => {
				const parents = FIELD_PARENTS[col.key] || [];
				const locked = parents.some((parent) => !(filterData?.[parent] || []).length);
				return (
				<RenderInputField
					key={col.key}
					column={col}
					data={filterData}
					setData={setFilterData}
					selectOptObjName={"select_options"}
					width={"100%"}
					longWidth={"300px"}
					disabled={locked}
				/>
				);
			})}
		</QueryFilterPanel>
	);
};

export default ReturnReport;
